import { inject, Injectable } from '@angular/core';
import { GenericApiOption, GenericApiService } from '@core/generic-api/data-access';
import { map, Observable, switchMap, tap } from 'rxjs';
import { BizflexFacade } from '../store';
import { LeadSubmissionResponse } from '../type/lead.type';
import { LeadFormModel, LeadModel } from '../model/lead.model';
import { GenericStorageApiService, StoragePublicLinkDto, StorageUploadLink } from '@portal/storage-api/data-access';
import { ApplicationUploadLinkDto } from '@portal/application/data-access';
import { SKIP_RECAPTCHA_INTERCEPTOR } from '@core/recaptcha/data-access';

@Injectable({ providedIn: 'root' })
export class LeadApiService extends GenericStorageApiService {
  private readonly apiService = inject(GenericApiService);
  private readonly bizflexFacade = inject(BizflexFacade);
  readonly UPLOAD_LINK = 'bez-lead-submissions/upload-link';
  readonly PUBLIC_LINK = 'bez-lead-submissions/link';

  getUploadLink(dto: ApplicationUploadLinkDto, requestOption?: GenericApiOption): Observable<StorageUploadLink> {
    return this.bizflexFacade.leadSubmissionToken$.pipe(
      switchMap(token => {
        const headers = this.apiService.createHeaders().set('Authorization', `Bearer ${token}`);
        headers.set(SKIP_RECAPTCHA_INTERCEPTOR, 'true');

        return this.api
          .post(this.UPLOAD_LINK, dto, { ...requestOption, requestOptions: { headers } })
          .pipe(map(res => StorageUploadLink.fromJson(res)));
      }),
    );
  }

  getPublicLink(data: StoragePublicLinkDto, requestOption?: GenericApiOption): Observable<string> {
    return this.bizflexFacade.leadSubmissionToken$.pipe(
      switchMap(token => {
        const headers = this.apiService.createHeaders().set('Authorization', `Bearer ${token}`);
        return this.api.get(this.PUBLIC_LINK, data, { ...requestOption, requestOptions: { headers } });
      }),
    );
  }

  calculateEstimateLoan(dto: { averageMonthlySales: number }): Observable<{ estimatedLoanLimit: number; averageMonthlySales: number }> {
    return this.apiService.post(this.getEndpoint('calculate-estimate-loan-limit'), dto);
  }

  createSubmission(dto: LeadFormModel): Observable<string> {
    return this.apiService.post(this.getEndpoint('create'), dto).pipe(tap(token => this.bizflexFacade.setLeadSubmissionToken(token)));
  }

  updateSubmission(dto: LeadFormModel): Observable<LeadModel> {
    return this.bizflexFacade.leadSubmissionToken$.pipe(
      switchMap(token => {
        const headers = this.apiService.createHeaders().set('Authorization', `Bearer ${token}`);
        return this.apiService.patch(this.getEndpoint(), dto, { requestOptions: { headers } });
      }),
    );
  }

  submitSubmission(dto: LeadModel): Observable<unknown> {
    return this.bizflexFacade.leadSubmissionToken$.pipe(
      switchMap(token => {
        const headers = this.apiService.createHeaders().set('Authorization', `Bearer ${token}`);
        return this.apiService.post(this.getEndpoint('submit'), dto, { requestOptions: { headers }, excludeFields: ['attachment'] });
      }),
    );
  }

  detailSubmission(): Observable<LeadSubmissionResponse> {
    return this.bizflexFacade.leadSubmissionToken$.pipe(
      switchMap(token => {
        const headers = this.apiService.createHeaders().set('Authorization', `Bearer ${token}`);
        return this.apiService
          .get<LeadSubmissionResponse>(this.getEndpoint(), null, { requestOptions: { headers } })
          .pipe(tap(res => this.bizflexFacade.setLeadSubmission(res.body)));
      }),
    );
  }

  private getEndpoint(...endpoint: string[]): string {
    return ['bez-lead-submissions', ...endpoint].join('/');
  }
}
